package com.example.socialmediaapp.network

import com.example.socialmediaapp.currentTimeMillis
import com.example.socialmediaapp.data.models.Chat
import com.example.socialmediaapp.data.models.ChatType
import com.example.socialmediaapp.data.models.Message
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

// ============================================
// API DTOs
// ============================================

@Serializable
data class ApiChat(
    val id: String,
    val name: String,
    val type: String = "direct",
    @SerialName("created_at") val createdAt: Long = 0L
) {
    fun toDomain() = Chat(
        id = id,
        name = name,
        type = when (type) {
            "group" -> ChatType.GROUP
            "channel" -> ChatType.CHANNEL
            else -> ChatType.DIRECT
        },
        participants = emptyList()
    )
}

@Serializable
data class ApiMessage(
    val id: String,
    @SerialName("chat_id") val chatId: String,
    @SerialName("sender_id") val senderId: String,
    @SerialName("sender_name") val senderName: String? = null,
    val content: String,
    val timestamp: Long,
    @SerialName("edited_at") val editedAt: Long? = null,
    @SerialName("deleted_at") val deletedAt: Long? = null
) {
    fun toDomain() = Message(
        id = id,
        chatId = chatId,
        senderId = senderId,
        senderName = senderName,
        content = content,
        timestamp = timestamp * 1000L,
        editedAt = editedAt?.let { it * 1000L },
        deletedAt = deletedAt?.let { it * 1000L }
    )
}

@Serializable
data class CreateChatRequest(
    val name: String,
    val type: String = "direct",
    val participantIds: List<String> = emptyList()
)

@Serializable
data class CreateChatResponse(val id: String = "")

@Serializable
data class SendMessageRequest(
    val content: String,
    @SerialName("expires_at") val expiresAt: Long? = null
)

@Serializable
data class SendMessageResponse(val id: String = "")

// ============================================
// CHAT REPOSITORY
// ============================================

class ChatRepository(private val api: ApiClient) {

    private val messageFlows = mutableMapOf<String, MutableStateFlow<List<Message>>>()

    fun messagesFlow(chatId: String): StateFlow<List<Message>> =
        messageFlows.getOrPut(chatId) { MutableStateFlow(emptyList()) }

    suspend fun getChats(): Result<List<Chat>> = runCatching {
        api.get<List<ApiChat>>("/api/chats").map { it.toDomain() }
    }

    suspend fun createChat(
        name: String,
        type: String = "direct",
        participantIds: List<String> = emptyList()
    ): Result<String> = runCatching {
        api.post<CreateChatResponse>("/api/chats", CreateChatRequest(name, type, participantIds)).id
    }

    suspend fun getMessages(chatId: String): Result<List<Message>> = runCatching {
        val messages = api.get<List<ApiMessage>>("/api/chats/$chatId/messages").map { it.toDomain() }
        messageFlows.getOrPut(chatId) { MutableStateFlow(emptyList()) }.value = messages
        messages
    }

    suspend fun sendMessage(
        chatId: String,
        content: String,
        expiresInSeconds: Long? = null
    ): Result<String> = runCatching {
        val expiresAt = expiresInSeconds?.let { (currentTimeMillis() / 1000) + it }
        api.post<SendMessageResponse>(
            "/api/chats/$chatId/messages",
            SendMessageRequest(content, expiresAt)
        ).id
    }

    fun onIncomingMessage(message: Message) {
        val flow = messageFlows.getOrPut(message.chatId) { MutableStateFlow(emptyList()) }
        flow.value = flow.value + message
    }
}
